import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';
import extractText from 'flarum/common/utils/extractText';

/** Characters a reply may have; mirrors StoryReplies::MAX_LENGTH. */
const MAX_LENGTH = 500;

/** How long the "sent" confirmation stays before the field returns. */
const SENT_NOTICE_MS = 2000;

/**
 * "Reply to <user>…" — a private message to the story's author, like
 * Instagram's reply field.
 *
 * The story is held while the field has focus, so it does not move on in the
 * middle of a sentence.
 *
 * Attrs:
 *   story   – the story being replied to
 *   onfocus – called when typing starts (the viewer pauses)
 *   onblur  – called when typing ends (the viewer resumes)
 */
export default class StoryReplyBar extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.reset();
  }

  onbeforeupdate(vnode) {
    super.onbeforeupdate(vnode);

    // Not keyed (the footer beside it is not), so the same instance moves on
    // with the viewer: a half-typed reply must not carry over to the next
    // story and be sent to the wrong person.
    if (this.attrs.story.id !== this.storyId) this.reset();
  }

  onremove(vnode) {
    super.onremove(vnode);
    clearTimeout(this.sentTimer);
  }

  reset() {
    this.storyId = this.attrs.story.id;
    this.text = '';
    this.sending = false;
    this.sent = false;
    // Why the last send failed. Shown here because Flarum's alerts open
    // beneath the full-screen viewer and would never be seen.
    this.error = null;
    clearTimeout(this.sentTimer);
  }

  view() {
    const user = this.attrs.story.user;
    const name = user.displayName || user.username;
    // Attributes need a plain string; trans() may return an array of parts.
    const placeholder = extractText(app.translator.trans('happydigital-stories.forum.reply_placeholder', { name }));
    const sendLabel = extractText(app.translator.trans('happydigital-stories.forum.reply_send'));

    if (this.sent) {
      return (
        <div className="StoryReplyBar StoryReplyBar--sent" aria-live="polite">
          {icon('fas fa-check')} {app.translator.trans('happydigital-stories.forum.reply_sent')}
        </div>
      );
    }

    return (
      <form
        className="StoryReplyBar"
        // The viewer pauses on press and resumes on release anywhere in it;
        // clicking into the field would otherwise resume the story right
        // after focus paused it.
        onmousedown={(e) => e.stopPropagation()}
        onmouseup={(e) => e.stopPropagation()}
        ontouchstart={(e) => e.stopPropagation()}
        ontouchend={(e) => e.stopPropagation()}
        onsubmit={(e) => {
          e.preventDefault();
          this.send();
        }}
      >
        {this.error ? <div className="StoryReplyBar-error">{this.error}</div> : null}
        <input
          className="StoryReplyBar-input"
          type="text"
          maxlength={MAX_LENGTH}
          value={this.text}
          disabled={this.sending}
          placeholder={placeholder}
          aria-label={placeholder}
          oninput={(e) => {
            this.text = e.target.value;
            this.error = null;
          }}
          onfocus={() => this.attrs.onfocus()}
          onblur={() => this.attrs.onblur()}
        />
        {this.text.trim() ? (
          <button
            className="StoryReplyBar-send"
            type="submit"
            disabled={this.sending}
            title={sendLabel}
            aria-label={sendLabel}
          >
            {icon(this.sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane')}
          </button>
        ) : null}
      </form>
    );
  }

  send() {
    const content = this.text.trim();

    if (!content || this.sending) return;

    // Captured now: the viewer may have moved on by the time the answer lands.
    const storyId = this.attrs.story.id;

    this.sending = true;
    this.error = null;

    app
      .request({
        method: 'POST',
        url: `/api/happydigital/stories/${storyId}/reply`,
        body: { content },
        errorHandler: (error) => {
          const errors = (error && error.response && error.response.errors) || [];
          const details = errors.map((e) => e.detail).filter(Boolean);

          this.error = details.length
            ? details.join(' ')
            : app.translator.trans('happydigital-stories.forum.reply_failed');
        },
      })
      .then(() => {
        if (this.storyId !== storyId) return;

        this.text = '';
        this.sending = false;
        this.sent = true;

        // The field loses focus with the send, so the story carries on.
        if (document.activeElement) document.activeElement.blur();

        this.sentTimer = setTimeout(() => {
          this.sent = false;
          m.redraw();
        }, SENT_NOTICE_MS);

        m.redraw();
      })
      .catch(() => {
        // The reason is shown by errorHandler; keep the text for another try.
        this.sending = false;
        m.redraw();
      });
  }
}
